import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate, typeLabel, projectInfo } from "@/components/shared";
import { ArrowLeft, MailPlus, Pencil, Trash2, Ban, CheckCircle2, BellRing, UserCog, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { startImpersonation } from "@/lib/impersonate";

const Row = ({ label, value, tid }) => (
  <div className="grid grid-cols-3 border-b border-slate-200 last:border-b-0">
    <div className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="px-4 py-3 col-span-2 text-sm" data-testid={tid}>{value || "-"}</div>
  </div>
);

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const fullAdmin = (authUser?.permissions || ["all"]).includes("all");
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [offers, setOffers] = useState(null);
  const [score, setScore] = useState(null);
  const [remindBusy, setRemindBusy] = useState(false);

  const sendSlowReminder = async () => {
    setRemindBusy(true);
    try {
      const { data } = await api.post(`/users/${id}/slow-reminder`);
      toast.success("Hatırlatma e-postası gönderildi");
      setUser((u) => ({ ...u, slow_reminder_at: data.slow_reminder_at }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemindBusy(false);
    }
  };

  const fmtHours = (h) => {
    if (h == null) return "—";
    if (h < 1) return `${Math.round(h * 60)} dk`;
    if (h < 48) return `${h.toFixed(1).replace(".", ",")} saat`;
    return `${(h / 24).toFixed(1).replace(".", ",")} gün`;
  };

  useEffect(() => {
    api.get(`/users/${id}`).then((r) => setUser(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get(`/users/${id}/offers`).then((r) => setOffers(r.data)).catch(() => setOffers([]));
    api.get(`/users/${id}/scorecard`).then((r) => setScore(r.data)).catch(() => {});
  }, [id]);

  const openEdit = () => {
    setForm({
      account_type: user.account_type, company_name: user.company_name,
      website: user.website || "", contact_name: user.contact_name, contact_email: user.contact_email,
      contact_phone: user.contact_phone,
    });
    setEditOpen(true);
  };

  const setField = (k, v) => {
    if (k === "contact_phone") {
      let val = v;
      if (!val.startsWith("+90")) val = "+90" + val.replace(/^\+?9?0?/, "");
      v = "+90" + val.slice(3).replace(/\D/g, "").slice(0, 10);
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/${id}`, form);
      setUser((u) => ({ ...u, ...data }));
      toast.success("Tedarikçi bilgileri güncellendi");
      setEditOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const revokeSessions = async () => {
    if (!window.confirm("Bu kullanıcının tüm cihazlardaki oturumları sonlandırılsın mı? Yeniden giriş yapması gerekir.")) return;
    try {
      await api.post(`/users/${id}/revoke-tokens`);
      toast.success("Kullanıcının tüm oturumları sonlandırıldı");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async () => {
    const next = user.is_active === false;
    try {
      await api.patch(`/users/${id}`, { is_active: next });
      setUser((u) => ({ ...u, is_active: next }));
      toast.success(next ? "Hesap aktifleştirildi" : "Hesap pasifleştirildi — artık giriş yapamaz");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Tedarikçi silindi");
      navigate("/admin/kullanicilar");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${id}/resend-link`);
      toast.success("Şifre oluşturma bağlantısı tekrar gönderildi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;
  const inactive = user.is_active === false;

  const editField = (key, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`e-${key}`}>{label}</Label>
      <Input id={`e-${key}`} data-testid={`edit-user-${key}`} value={form[key]} onChange={(e) => setField(key, e.target.value)} className="rounded-none bg-[#F5F7FA] border-slate-200" {...props} />
    </div>
  );

  return (
    <div className="space-y-8 fade-in max-w-3xl">
      <div>
        <Link to="/admin/kullanicilar" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-users">
          <ArrowLeft size={15} /> Tedarikçilere Dön
        </Link>
        <div className="flex items-end justify-between gap-4 mt-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tedarikçi Detayı</div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="user-detail-company">{user.company_name}</h1>
              {inactive && <Badge variant="outline" className="rounded-none text-xs bg-red-500/10 text-red-500 border-red-500/20" data-testid="inactive-badge">Pasif</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {fullAdmin && (
              <Button
                variant="outline"
                onClick={async () => { try { await startImpersonation(id); } catch (e) { toast.error(apiError(e)); } }}
                data-testid="impersonate-user-button"
                className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-2 transition-colors duration-200"
              >
                <UserCog size={14} /> Bu Hesaba Geç
              </Button>
            )}
            <Button variant="outline" onClick={openEdit} data-testid="edit-user-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200">
              <Pencil size={14} /> Düzenle
            </Button>
            <Button variant="outline" onClick={toggleActive} data-testid="toggle-active-button" className={`rounded-none gap-2 transition-colors duration-200 ${inactive ? "border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10" : "border-yellow-500/40 text-yellow-600 hover:bg-yellow-500/10"}`}>
              {inactive ? <><CheckCircle2 size={14} /> Aktifleştir</> : <><Ban size={14} /> Pasifleştir</>}
            </Button>
            <Button variant="outline" onClick={revokeSessions} data-testid="revoke-sessions-button" className="rounded-none border-orange-300 text-orange-600 hover:bg-orange-50 gap-2 transition-colors duration-200">
              <ShieldOff size={14} /> Oturumları Sonlandır
            </Button>
            <Button variant="outline" onClick={() => setDelOpen(true)} data-testid="delete-user-button" className="rounded-none border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2 transition-colors duration-200">
              <Trash2 size={14} /> Sil
            </Button>
          </div>
        </div>
      </div>

      {score && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="supplier-scorecard">
          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Kazanma Oranı</div>
            <div className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="score-win-rate">%{score.win_rate}</div>
            <div className="text-xs text-slate-500 mt-0.5">{score.won_count} kazanılan / {score.offer_count} teklif</div>
            {score.category_avg && (
              <div className="text-[11px] text-slate-400 mt-1 pt-1 border-t border-slate-100" data-testid="cat-win-rate">Kategori ort: %{score.category_avg.win_rate}</div>
            )}
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Yanıt Oranı</div>
            <div className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="score-response-rate">%{score.response_rate}</div>
            <div className="text-xs text-slate-500 mt-0.5">{score.offer_count} teklif / {score.sent_requests} talep</div>
          </div>
          <div className={`border p-4 ${score.is_slow ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${score.is_slow ? "text-red-400" : "text-slate-400"}`}>Ort. Yanıt Süresi</div>
            <div className={`font-heading text-3xl font-extrabold tracking-tight mt-1 ${score.is_slow ? "text-red-600" : ""}`} data-testid="score-response-time">{fmtHours(score.avg_response_hours)}</div>
            <div className={`text-xs mt-0.5 ${score.is_slow ? "text-red-500 font-medium" : "text-slate-500"}`} data-testid="score-response-note">
              {score.is_slow ? `Yavaş — genel ort. ${fmtHours(score.fleet_avg_response_hours)}` : "talepten teklife geçen süre"}
            </div>
            {score.category_avg?.avg_response_hours != null && (
              <div className={`text-[11px] mt-1 pt-1 border-t ${score.is_slow ? "text-red-400 border-red-100" : "text-slate-400 border-slate-100"}`} data-testid="cat-response-time">
                Kategori ort: {fmtHours(score.category_avg.avg_response_hours)}
              </div>
            )}
            {score.is_slow && (
              <>
                <button
                  onClick={sendSlowReminder}
                  disabled={remindBusy}
                  data-testid="slow-reminder-button"
                  className="mt-2 inline-flex items-center gap-1.5 border border-red-300 bg-white text-red-600 hover:bg-red-100 text-[11px] px-2.5 py-1.5 transition-colors duration-200 disabled:opacity-50"
                >
                  <BellRing size={11} /> {remindBusy ? "Gönderiliyor..." : "Hatırlatma Gönder"}
                </button>
                {user.slow_reminder_at && (
                  <div className="text-[10px] text-red-400 mt-1" data-testid="slow-reminded-at">
                    Hatırlatıldı: {new Date(user.slow_reminder_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Ort. Teklif Fiyatı</div>
            {score.avg_prices.length === 0 ? (
              <div className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="score-avg-price">—</div>
            ) : (
              <div className="mt-1 space-y-0.5" data-testid="score-avg-price">
                {score.avg_prices.map((p) => (
                  <div key={p.currency} className="font-heading text-lg font-extrabold tracking-tight leading-tight">
                    {Math.round(p.avg).toLocaleString("tr-TR")} <span className="text-xs font-medium text-slate-500">{p.currency} ({p.count})</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-0.5">para birimi bazında</div>
            {score.category_avg?.avg_prices?.length > 0 && (
              <div className="text-[11px] text-slate-400 mt-1 pt-1 border-t border-slate-100" data-testid="cat-avg-price">
                Kategori ort: {score.category_avg.avg_prices.map((p) => `${Math.round(p.avg).toLocaleString("tr-TR")} ${p.currency}`).join(" · ")}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white">
        <Row label="Kullanıcı Adı" value={user.username} tid="detail-username" />
        <Row
          label="Hesap Tipi"
          tid="detail-account-type"
          value={<Badge variant="outline" className="rounded-none text-xs border-slate-300 text-slate-600">{typeLabel(user.account_type)}</Badge>}
        />
        <Row label="Firma Adı" value={user.company_name} tid="detail-company-name" />
        <Row
          label="Firma Web Sitesi"
          tid="detail-website"
          value={user.website ? (
            <a href={user.website.startsWith("http") ? user.website : `https://${user.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              {user.website}
            </a>
          ) : "-"}
        />
        <Row label="Yetkili Kişi" value={user.contact_name} tid="detail-contact-name" />
        <Row label="Yetkili E-posta" value={user.contact_email} tid="detail-contact-email" />
        <Row label="Yetkili Telefon" value={user.contact_phone} tid="detail-contact-phone" />
        <Row
          label="Durum"
          tid="detail-active-status"
          value={
            <Badge variant="outline" className={`rounded-none text-xs ${inactive ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"}`}>
              {inactive ? "Pasif — giriş yapamaz" : "Aktif"}
            </Badge>
          }
        />
        <Row
          label="Şifre Durumu"
          tid="detail-password-status"
          value={
            <Badge variant="outline" className={`rounded-none text-xs ${user.password_set ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}`}>
              {user.password_set ? "Kendi şifresini oluşturdu" : "Şifre oluşturması bekleniyor"}
            </Badge>
          }
        />
        <Row label="Kayıt Tarihi" value={formatDate(user.created_at)} tid="detail-created-at" />
        <Row label="Gönderilen Talep" value={String(user.request_count)} tid="detail-request-count" />
        <Row label="Verdiği Teklif" value={String(user.offer_count)} tid="detail-offer-count" />
      </div>

      <Button variant="outline" onClick={resend} disabled={busy} data-testid="resend-link-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2">
        <MailPlus size={15} /> {busy ? "Gönderiliyor..." : "Şifre Bağlantısını Tekrar Gönder"}
      </Button>

      <div className="border border-slate-200 bg-white" data-testid="user-offer-history">
        <div className="px-4 py-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
          Teklif Geçmişi {offers ? `(${offers.length})` : ""}
        </div>
        {!offers ? (
          <p className="px-4 py-6 text-sm text-slate-500">Yükleniyor...</p>
        ) : offers.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="no-offer-history">Bu tedarikçi henüz teklif vermedi.</p>
        ) : (
          offers.map((o) => {
            const meta = {
              pending: { label: "Değerlendirmede", cls: "bg-slate-100 text-slate-600 border-slate-300" },
              accepted: { label: "Kabul edildi", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
              rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
              awaiting: { label: "Beklemede", cls: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
              no_offer: { label: "Teklif Vermedi", cls: "bg-red-50 text-red-400 border-red-200" },
            }[o.status] || { label: o.status, cls: "bg-slate-100 text-slate-600 border-slate-300" };
            const inner = (
              <>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{o.request_title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {o.project && <>{projectInfo(o.project)} · </>}
                    {formatDate(o.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-5 shrink-0">
                  <div className="hidden sm:block text-right w-20" data-testid={`offer-rank-${o.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Sıra</div>
                    <div className={`text-sm font-semibold ${o.rank === 1 ? "text-emerald-600" : "text-slate-700"}`}>
                      {o.rank ? `${o.rank}. / ${o.total_offers}` : "—"}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right w-32" data-testid={`offer-lowest-${o.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">En Düşük</div>
                    <div className={`text-sm font-medium ${o.rank === 1 ? "text-emerald-600" : "text-slate-600"}`}>
                      {o.lowest_price != null ? `${o.lowest_price.toLocaleString("tr-TR")} ${o.lowest_currency}` : "—"}
                    </div>
                  </div>
                  <div className="text-right w-36">
                    <div className="text-sm font-semibold">{o.price != null ? `${o.price.toLocaleString("tr-TR")} ${o.currency}` : "—"}</div>
                    <Badge variant="outline" className={`rounded-none text-[10px] mt-1 ${meta.cls}`}>{meta.label}</Badge>
                  </div>
                </div>
              </>
            );
            return o.request_exists ? (
              <button
                key={o.id}
                onClick={() => navigate(`/admin/teklifler/${o.request_id}`)}
                data-testid={`offer-history-row-${o.id}`}
                className="w-full text-left px-4 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
              >
                {inner}
              </button>
            ) : (
              <div key={o.id} data-testid={`offer-history-row-${o.id}`} className="px-4 py-3.5 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3 opacity-60">
                {inner}
              </div>
            );
          })
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Tedarikçiyi Düzenle</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Hesap Tipi</Label>
                <Select value={form.account_type} onValueChange={(v) => { if (v) setField("account_type", v); }}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="edit-user-account-type">
                    <SelectValue>{form.account_type}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editField("company_name", "Firma Adı")}
              {editField("website", "Firma Web Sitesi")}
              {editField("contact_name", "Yetkili Kişi Adı Soyadı")}
              {editField("contact_email", "Yetkili Kişi E-postası")}
              {editField("contact_phone", "Yetkili Kişi Telefonu")}
              <p className="text-xs text-slate-500">Kullanıcı adı, yetkili kişinin e-posta adresiyle otomatik eşleşir; e-postayı değiştirirseniz kullanıcı adı da güncellenir.</p>
              <Button onClick={saveEdit} disabled={saving} data-testid="edit-user-save" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Tedarikçiyi sil</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              <span className="text-slate-900 font-medium">{user.company_name}</span> hesabını kalıcı olarak silmek istediğinize emin misiniz?
              Verdiği <span className="text-slate-900 font-medium">{user.offer_count} teklif</span> de silinir ve taleplerden çıkarılır. Bu işlem geri alınamaz.
              {user.offer_count > 0 && <> Geçmişi korumak istiyorsanız silmek yerine <span className="text-slate-900 font-medium">Pasifleştir</span> kullanın.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-200 hover:bg-slate-100" data-testid="delete-user-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-500 text-white" data-testid="delete-user-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
